import React, { useState } from 'react';
import PropTypes from 'prop-types';

/**
 * CollapsibleSection Component
 * Section with show more/less functionality - Enhanced UX
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Content to display (can be array or single element)
 * @param {string} [props.title] - Section title
 * @param {boolean} [props.defaultExpanded] - Default expanded state
 * @param {number} [props.initialVisible] - Number of items to show initially (when children is array). If 0, shows all but allows collapse.
 * @param {React.ReactNode} [props.summary] - Summary content shown when collapsed
 * @param {boolean} [props.showCount] - Whether to show count in button
 */
const CollapsibleSection = ({
  children,
  title = '',
  defaultExpanded = false,
  initialVisible = 3,
  summary = null,
  showCount = true
}) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  
  // Convert children to array for easier handling
  const childArray = React.Children.toArray(children);
  
  // If initialVisible is 0, show all by default but allow collapsing
  const effectiveInitialVisible = initialVisible === 0 ? childArray.length : initialVisible;
  const hasMore = childArray.length > effectiveInitialVisible;
  
  const displayChildren = isExpanded 
    ? children 
    : (hasMore ? childArray.slice(0, effectiveInitialVisible) : children);

  const remainingCount = childArray.length - effectiveInitialVisible;

  return (
    <div className="collapsible-section">
      {title && (
        <div className="collapsible-header">
          <h3 className="collapsible-title">{title}</h3>
          {hasMore && (
            <button 
              className="collapsible-toggle"
              onClick={(e) => {
                e.stopPropagation();
                setIsExpanded(!isExpanded);
              }}
              aria-label={isExpanded ? 'Show less' : 'Show more'}
              aria-expanded={isExpanded}
            >
              {isExpanded ? (
                <>
                  <i className="bi bi-chevron-up icon-brand"></i>
                  <span>Show Less</span>
                </>
              ) : (
                <>
                  <i className="bi bi-chevron-down icon-brand"></i>
                  <span>
                    {showCount ? `Show ${remainingCount} More` : 'Show More'}
                  </span>
                </>
              )}
            </button>
          )}
        </div>
      )}
      
      {!isExpanded && summary && (
        <div className="collapsible-summary">{summary}</div>
      )}
      
      <div className={`collapsible-content ${isExpanded ? 'expanded' : ''}`}>
        {displayChildren}
      </div>
      
      {!title && hasMore && (
        <div className="collapsible-footer">
          <button 
            className="collapsible-toggle"
            onClick={() => setIsExpanded(!isExpanded)}
            aria-label={isExpanded ? 'Show less' : 'Show more'}
            aria-expanded={isExpanded}
          >
            {isExpanded ? (
              <>
                <i className="bi bi-chevron-up icon-brand"></i>
                <span>Show Less</span>
              </>
            ) : (
              <>
                <i className="bi bi-chevron-down icon-brand"></i>
                <span>
                  {showCount ? `Show ${remainingCount} More` : 'Show More'}
                </span>
              </>
            )}
          </button>
        </div>
      )}
    </div>
  );
};

CollapsibleSection.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node
  ]).isRequired,
  title: PropTypes.string,
  defaultExpanded: PropTypes.bool,
  initialVisible: PropTypes.number,
  summary: PropTypes.node,
  showCount: PropTypes.bool
};

export default CollapsibleSection;

